/*
 * SPDX-FileCopyrightText: © 2024 Schneider Electric
 *
 * SPDX-License-Identifier: Apache-2.0
 */

@import url('../shared.css');

:host {
  display: block;
}

.qds-disabled {
  opacity: var(--qds-theme-disabled);
  pointer-events: none;
}

.qds-list-item,
.qds-indented-content {
  align-items: center;
  box-sizing: border-box;
  border-radius: var(--qds-control-border-radius);
}

.qds-list-action {
  pointer-events: none;
}

.qds-indented-content {
  display: flex;
  flex: 1 0;
  align-self: stretch;
}

.qds-destination {
  transform: scaleX(var(--qds-direction-factor, 1));
}

.qds-icon {
  color: var(--qds-theme-text-standard);
}

.qds-avatar {
  border-radius: var(--qds-control-rounded-border-radius);
  flex-shrink: 0;
}

.qds-picture {
  flex-shrink: 0;
}

.qds-texts {
  justify-items: center;
  display: flex;
  flex: 1 0;
  flex-direction: column;
  padding: 0;
  position: relative;
}

.qds-input {
  appearance: none;
  background: none;
  border: none;
  border-radius: var(--qds-control-border-radius);
  inset: 0;
  margin: 0;
  outline: none;
  position: absolute;
}

.qds-title {
  color: var(--qds-theme-title);
  display: -webkit-box;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 2;
  overflow: hidden;
}

.qds-subtitle {
  color: var(--qds-theme-subtitle);
  display: -webkit-box;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 5;
  overflow: hidden;
}

.qds-kicker {
  color: var(--qds-theme-kicker);
  display: -webkit-box;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 1;
  overflow: hidden;
}

[data-size='small'] {
  .qds-indented-content {
    gap: var(--qds-list-item-small-gap-internal);
    min-height: var(--qds-list-item-small-height);
    padding: var(--qds-list-item-small-padding-vertical)
      var(--qds-list-item-small-padding-horizontal);
  }

  .qds-icon {
    font-size: var(--qds-control-small-icon-size);
  }

  .qds-main-icon {
    font-size: var(--qds-list-item-small-icon-size);
  }

  .qds-texts {
    gap: var(--qds-list-item-small-titles-gap);
  }

  .qds-kicker {
    font: var(--qds-list-item-small-kicker);
  }

  .qds-title {
    font: var(--qds-list-item-small-title);
  }

  .qds-subtitle {
    font: var(--qds-list-item-small-subtitle);
  }

  .qds-avatar,
  .qds-picture {
    height: var(--qds-list-item-small-picture-size);
    width: var(--qds-list-item-small-picture-size);
  }
}

[data-size='standard'] {
  .qds-indented-content {
    gap: var(--qds-list-item-standard-gap-internal);
    min-height: var(--qds-list-item-standard-height);
    padding: var(--qds-list-item-standard-padding-vertical)
      var(--qds-list-item-standard-padding-horizontal);
  }

  .qds-icon {
    font-size: var(--qds-control-standard-icon-size);
  }

  .qds-main-icon {
    font-size: var(--qds-list-item-standard-icon-size);
  }

  .qds-texts {
    gap: var(--qds-list-item-standard-titles-gap);
  }

  .qds-kicker {
    font: var(--qds-list-item-standard-kicker);
  }

  .qds-title {
    font: var(--qds-list-item-standard-title);
  }

  .qds-subtitle {
    font: var(--qds-list-item-standard-subtitle);
  }

  .qds-avatar,
  .qds-picture {
    height: var(--qds-list-item-standard-picture-size);
    width: var(--qds-list-item-standard-picture-size);
  }
}

[data-size='large'] {
  .qds-indented-content {
    gap: var(--qds-list-item-large-gap-internal);
    min-height: var(--qds-list-item-large-height);
    padding: var(--qds-list-item-large-padding-vertical)
      var(--qds-list-item-large-padding-horizontal);
  }

  .qds-icon {
    font-size: var(--qds-control-large-icon-size);
  }

  .qds-main-icon {
    font-size: var(--qds-list-item-large-icon-size);
  }

  .qds-texts {
    gap: var(--qds-list-item-large-titles-gap);
  }

  .qds-kicker {
    font: var(--qds-list-item-large-kicker);
  }

  .qds-title {
    font: var(--qds-list-item-large-title);
  }

  .qds-subtitle {
    font: var(--qds-list-item-large-subtitle);
  }

  .qds-avatar,
  .qds-picture {
    height: var(--qds-list-item-large-picture-size);
    width: var(--qds-list-item-large-picture-size);
  }
}

.qds-list-item {
  display: block;

  &:hover {
    background-color: var(--qds-theme-interactive-background-hover);
  }

  &:active {
    background-color: var(--qds-theme-interactive-background-pressed);
  }

  &:has(input:focus-visible) {
    outline: var(--qds-theme-focus-border) solid var(--qds-focus-border-width);
    outline-offset: var(--qds-focus-border-offset);
  }
}

.qds-selected {
  &.qds-list-item {
    background-color: var(--qds-theme-interactive-background-selected);

    &:hover {
      background-color: var(--qds-theme-interactive-background-selected-hover);
    }

    &:active {
      background-color: var(
        --qds-theme-interactive-background-selected-pressed
      );
    }
  }

  &[data-size='small'] .qds-title {
    font: var(--qds-list-item-small-title-emphasized);
  }

  &[data-size='standard'] .qds-title {
    font: var(--qds-list-item-standard-title-emphasized);
  }

  &[data-size='large'] .qds-title {
    font: var(--qds-list-item-large-title-emphasized);
  }
}
